/**
 * AIChatAgentConfigProvider — owns the AgentConfig slice via its own useReducer.
 *
 * One of the four nested slice providers mounted by AIChatProvider. Consumers read the slice
 * through the named hook useAIChatAgentConfig(); there is no merged value, each slice
 * context is subscribed to directly.
 *
 * Action gating: `areActionsEnabled` (AIChatProvider passes isAIEnabled) gates the exposed
 * action set at call time via useGatedActions — state stays readable while writes are suppressed,
 * and the exposed identities never change. The slice itself is generic and knows nothing about
 * AI capability.
 */
import PropTypes from 'prop-types';
import React, { createContext, useMemo, useReducer } from 'react';
import { useGatedActions } from '../useGatedActions';
import { AGENT_CONFIG_ACTIONS, agentConfigReducer, getInitialAgentConfigState } from './aiChatAgentConfigReducer';
import type { AgentConfigState } from './aiChatAgentConfigTypes';

export type AIChatAgentConfigActions = {
  setSystemPrompt: (prompt: string) => void;
  setModel: (model: string) => void;
  setTemperature: (temperature: number) => void;
  setMaxTokens: (maxTokens: number) => void;
  setEnableExternalSearch: (enabled: boolean) => void;
};

export type AIChatAgentConfigValue = AgentConfigState & AIChatAgentConfigActions;

export const AIChatAgentConfigContext = createContext<AIChatAgentConfigValue | null>(null);

type AIChatAgentConfigProviderProps = {
  areActionsEnabled?: boolean;
  children: React.ReactNode;
  initialState?: Partial<AgentConfigState>;
};

function AIChatAgentConfigProvider({ areActionsEnabled = true, children, initialState = {} }: AIChatAgentConfigProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(agentConfigReducer, initialState, getInitialAgentConfigState);

  // Setters memoized once (deps `[]`) so their identity is stable for the component's lifetime —
  // `dispatch` is stable, so there is no reason to rebuild them per state change. Keeping them out
  // of the `[state]`-keyed value memo avoids churning consumer useCallback/useEffect deps.
  const actions = useMemo<AIChatAgentConfigActions>(
    () => ({
      setSystemPrompt: (prompt: string) => dispatch({ type: AGENT_CONFIG_ACTIONS.SET_SYSTEM_PROMPT, payload: prompt }),
      setModel: (model: string) => dispatch({ type: AGENT_CONFIG_ACTIONS.SET_MODEL, payload: model }),
      setTemperature: (temperature: number) => dispatch({ type: AGENT_CONFIG_ACTIONS.SET_TEMPERATURE, payload: temperature }),
      setMaxTokens: (maxTokens: number) => dispatch({ type: AGENT_CONFIG_ACTIONS.SET_MAX_TOKENS, payload: maxTokens }),
      setEnableExternalSearch: (enabled: boolean) => dispatch({ type: AGENT_CONFIG_ACTIONS.SET_ENABLE_EXTERNAL_SEARCH, payload: enabled }),
    }),
    []
  );

  // Call-time gating keeps the exposed identities permanently stable: consumers that capture the
  // set once (empty-dep callbacks/effects) keep dispatching correctly after areActionsEnabled flips.
  const exposedActions = useGatedActions(actions, areActionsEnabled);

  const value = useMemo<AIChatAgentConfigValue>(() => ({ ...state, ...exposedActions }), [state, exposedActions]);

  return <AIChatAgentConfigContext.Provider value={value}>{children}</AIChatAgentConfigContext.Provider>;
}

AIChatAgentConfigProvider.propTypes = {
  areActionsEnabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  initialState: PropTypes.object,
};

export default AIChatAgentConfigProvider;
